/** Structural edits of a record's collections as draft actions (the tour plan §3 "New
 *  rows"): each is ONE `SET` — one undo step — over the membership list and the rows' own
 *  entries.
 *
 *  - add: the list gains a new negative id (`nextTempId` over the kind's membership lists,
 *    `spec.memberships`; the SET's `issued` — never issued again); the row's fields read
 *    their defaults (`defaultValueOf`) until
 *    edited, and `seed` values (a picked category, an inherited row's flags) become its
 *    first entries;
 *  - remove: the list loses the id and the row's entries are reverted with it (a new
 *    row vanishes entirely; a stored one is deleted on save);
 *  - restore: a stored row back into the list, at its stored position;
 *  - move: the list's order.
 *
 *  Pure: they read the draft state and the baselines, and return the action. The list
 *  edits themselves (`withId`, `withoutId`, `movedId`, `placedAsStored`, `idListRows`) are
 *  plain functions over an id list, shared with a field whose value IS an ordered id list
 *  (`OrderedIdListWidget`: an activity's nearby activities). */
import { recordKey, type DraftAction, type DraftChange, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';

import { assertKindMemberships, nextTempId } from './membership';
import type { CollectionSpec } from './spec';

type BaselineOf = (rec: RecordKey, path: string) => unknown;

function listOf(state: DraftState, baselineOf: BaselineOf, ownerRec: RecordKey, spec: CollectionSpec): number[] {
  const entry = state.entries[ownerRec]?.[spec.membership];
  const v = entry ? entry.to : baselineOf(ownerRec, spec.membership);
  return Array.isArray(v) ? (v as number[]) : [];
}

const listChange = (baselineOf: BaselineOf, ownerRec: RecordKey, spec: CollectionSpec, to: number[]): DraftChange => ({
  rec: ownerRec,
  path: spec.membership,
  from: baselineOf(ownerRec, spec.membership),
  to,
});

/** Add a row (at the end). `seed`: path → value entries of the new row. */
export function addRow(
  state: DraftState,
  baselineOf: BaselineOf,
  ownerRec: RecordKey,
  spec: CollectionSpec,
  { seed = {}, label }: { seed?: Record<string, unknown>; label?: string } = {},
): { id: number; action: Extract<DraftAction, { type: 'SET' }> } {
  assertKindMemberships(ownerRec, spec.memberships);
  const id = nextTempId(state, spec.memberships);
  const rec = recordKey(spec.entity, id);
  const changes: DraftChange[] = [listChange(baselineOf, ownerRec, spec, [...listOf(state, baselineOf, ownerRec, spec), id])];
  for (const [path, to] of Object.entries(seed)) {
    changes.push({ rec, path, from: spec.defaults[path], to });
  }
  return { id, action: { type: 'SET', changes, issued: [id], ...(label ? { label } : {}) } };
}

/** Add several rows at the end in ONE step (an import from another record). */
export function addRows(
  state: DraftState,
  baselineOf: BaselineOf,
  ownerRec: RecordKey,
  spec: CollectionSpec,
  seeds: Array<Record<string, unknown>>,
  label?: string,
): { ids: number[]; action: Extract<DraftAction, { type: 'SET' }> } {
  assertKindMemberships(ownerRec, spec.memberships);
  const first = nextTempId(state, spec.memberships);
  const ids = seeds.map((_, i) => first - i);
  const changes: DraftChange[] = [listChange(baselineOf, ownerRec, spec, [...listOf(state, baselineOf, ownerRec, spec), ...ids])];
  seeds.forEach((seed, i) => {
    const rec = recordKey(spec.entity, ids[i]!);
    for (const [path, to] of Object.entries(seed)) changes.push({ rec, path, from: spec.defaults[path], to });
  });
  return { ids, action: { type: 'SET', changes, issued: ids, ...(label ? { label } : {}) } };
}

/** Remove several rows in ONE step (a bulk remove): off the list, their entries reverted. */
export function removeRows(
  state: DraftState,
  baselineOf: BaselineOf,
  ownerRec: RecordKey,
  spec: CollectionSpec,
  ids: readonly number[],
  label?: string,
): Extract<DraftAction, { type: 'SET' }> {
  const gone = new Set(ids);
  const changes: DraftChange[] = [
    listChange(
      baselineOf,
      ownerRec,
      spec,
      listOf(state, baselineOf, ownerRec, spec).filter((x) => !gone.has(x)),
    ),
  ];
  for (const id of ids) {
    const rec = recordKey(spec.entity, id);
    for (const [path, entry] of Object.entries(state.entries[rec] ?? {})) {
      changes.push({ rec, path, from: entry.from, to: entry.stale ? entry.stale.current : entry.from });
    }
  }
  return { type: 'SET', changes, ...(label ? { label } : {}) };
}

/** Remove a row: off the list, its own entries reverted with it. */
export function removeRow(
  state: DraftState,
  baselineOf: BaselineOf,
  ownerRec: RecordKey,
  spec: CollectionSpec,
  id: number,
  label?: string,
): Extract<DraftAction, { type: 'SET' }> {
  const rec = recordKey(spec.entity, id);
  const changes: DraftChange[] = [
    listChange(
      baselineOf,
      ownerRec,
      spec,
      listOf(state, baselineOf, ownerRec, spec).filter((x) => x !== id),
    ),
  ];
  for (const [path, entry] of Object.entries(state.entries[rec] ?? {})) {
    // Back to what is stored: a stale entry agrees with the server's value.
    changes.push({ rec, path, from: entry.from, to: entry.stale ? entry.stale.current : entry.from });
  }
  return { type: 'SET', changes, ...(label ? { label } : {}) };
}

/** Put a stored row back into the list, at its stored position. */
export function restoreRow(
  state: DraftState,
  baselineOf: BaselineOf,
  ownerRec: RecordKey,
  spec: CollectionSpec,
  id: number,
  label?: string,
): Extract<DraftAction, { type: 'SET' }> {
  const list = listOf(state, baselineOf, ownerRec, spec);
  const stored = baselineOf(ownerRec, spec.membership);
  const pos = Array.isArray(stored) ? (stored as number[]).indexOf(id) : -1;
  const next = [...list];
  next.splice(pos < 0 ? next.length : Math.min(pos, next.length), 0, id);
  return { type: 'SET', changes: [listChange(baselineOf, ownerRec, spec, next)], ...(label ? { label } : {}) };
}

/** Move a row one place up (`-1`) or down (`+1`). Null when it cannot move. */
export function moveRow(
  state: DraftState,
  baselineOf: BaselineOf,
  ownerRec: RecordKey,
  spec: CollectionSpec,
  id: number,
  delta: -1 | 1,
  label?: string,
): Extract<DraftAction, { type: 'SET' }> | null {
  const next = movedId(listOf(state, baselineOf, ownerRec, spec), id, delta);
  if (!next) return null;
  return { type: 'SET', changes: [listChange(baselineOf, ownerRec, spec, next)], ...(label ? { label } : {}) };
}

/** The rows a table shows: the list (the draft's in edit mode), then — in edit mode — the
 *  stored rows the draft removed, so they can be restored until the save. */
export function tableRows(
  state: DraftState,
  baselineOf: BaselineOf,
  ownerRec: RecordKey,
  spec: CollectionSpec,
  editing: boolean,
): Array<{ id: number; removed: boolean }> {
  const stored = baselineOf(ownerRec, spec.membership);
  const storedList = Array.isArray(stored) ? (stored as number[]) : [];
  return idListRows(storedList, editing ? listOf(state, baselineOf, ownerRec, spec) : storedList, editing);
}

// ---------------------------------------------------------------------------
// Id-list edits (pure, on the list alone)
// ---------------------------------------------------------------------------

/** The ids of a list value (anything else: none). */
export function asIdList(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((x): x is number => typeof x === 'number') : [];
}

/** `list` with `id` at the end (as it is when `id` is in it already). */
export function withId(list: readonly number[], id: number): number[] {
  return list.includes(id) ? [...list] : [...list, id];
}

/** `list` without `id`. */
export function withoutId(list: readonly number[], id: number): number[] {
  return list.filter((x) => x !== id);
}

/** `list` with `id` one place up (`-1`) or down (`+1`); null when it cannot move. */
export function movedId(list: readonly number[], id: number, delta: -1 | 1): number[] | null {
  const i = list.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= list.length) return null;
  const next = [...list];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

/** `list` with a stored `id` (back) where the stored order puts it: after the nearest id
 *  stored before it that the list still has, else before the nearest one stored after it,
 *  else at the end. Restoring a removed id and taking back a move both land it there. */
export function placedAsStored(list: readonly number[], stored: readonly number[], id: number): number[] {
  const next = withoutId(list, id);
  const at = stored.indexOf(id);
  for (let k = at - 1; k >= 0; k -= 1) {
    const i = next.indexOf(stored[k]!);
    if (i >= 0) return [...next.slice(0, i + 1), id, ...next.slice(i + 1)];
  }
  for (let k = at + 1; at >= 0 && k < stored.length; k += 1) {
    const i = next.indexOf(stored[k]!);
    if (i >= 0) return [...next.slice(0, i), id, ...next.slice(i)];
  }
  return [...next, id];
}

/** The rows a list shows: `list` (the draft's in edit mode), then — in edit mode — the
 *  stored ids it dropped, so they can be restored until the save. */
export function idListRows(
  stored: readonly number[],
  list: readonly number[],
  editing: boolean,
): Array<{ id: number; removed: boolean }> {
  if (!editing) return stored.map((id) => ({ id, removed: false }));
  return [
    ...list.map((id) => ({ id, removed: false })),
    ...stored.filter((id) => !list.includes(id)).map((id) => ({ id, removed: true })),
  ];
}
